import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import PeoplePicker, { Person } from '../PeoplePicker';
import {
  ProjectMember, ProjectMemberRole, ROLE_LABEL, LAST_PM_MESSAGE,
  getProjectMembers, createProjectMember,
  removeProjectMemberGuarded, setProjectMemberRoleGuarded,
} from '../../services/projectMemberService';
import { getGroupMembers, GroupMember } from '../../services/groupService';

const ROLE_OPTIONS = [
  ProjectMemberRole.ProjectManager, ProjectMemberRole.TeamMember,
  ProjectMemberRole.Stakeholder, ProjectMemberRole.Observer,
];

// PM rows sort first so the manager(s) are never buried in the roster.
function sortMembers(ms: ProjectMember[]): ProjectMember[] {
  return [...ms].sort((a, b) => {
    const ap = a.dagrun_pm_role === ProjectMemberRole.ProjectManager ? 0 : 1;
    const bp = b.dagrun_pm_role === ProjectMemberRole.ProjectManager ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return (a.dagrun_pm_username ?? '').localeCompare(b.dagrun_pm_username ?? '');
  });
}

/**
 * Project Settings → Team & Roles. Lists every project member with their role,
 * Project Manager(s) visually distinguished. When canManage (the viewer is a PM),
 * supports adding members (PeoplePicker + role), changing roles, and removing —
 * all through the guarded service ops that refuse to leave a project with zero PMs.
 */
export default function TeamRolesSection({ pca, projectId, groupId, canManage }: {
  pca: IPublicClientApplication; projectId: string; groupId?: string | null; canManage: boolean;
}) {
  const [members, setMembers] = useState<ProjectMember[] | null>(null);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [worker, setWorker] = useState<Person | null>(null);
  const [addRole, setAddRole] = useState<number>(ProjectMemberRole.TeamMember);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try { setMembers(sortMembers(await getProjectMembers(pca, projectId))); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load members.'); setMembers([]); }
  }, [pca, projectId]);
  useEffect(() => { load(); }, [load]);

  // Auto-populate from the linked Microsoft 365 Group: pull its membership so
  // the roster reflects the Group even before anyone is added as a project
  // member. Best-effort — a Graph failure just leaves the stored roster.
  useEffect(() => {
    if (!groupId) { setGroupMembers([]); return; }
    let cancelled = false;
    getGroupMembers(pca, groupId)
      .then(gm => { if (!cancelled) setGroupMembers(gm); })
      .catch(() => { if (!cancelled) setGroupMembers([]); });
    return () => { cancelled = true; };
  }, [pca, groupId]);

  const pmCount = (members ?? []).filter(m => m.dagrun_pm_role === ProjectMemberRole.ProjectManager).length;

  // Group members who are not already stored project members — shown as
  // "from the Group" rows a PM can promote to real project members.
  const storedIds = new Set((members ?? []).map(m => (m.dagrun_pm_userid ?? '').toLowerCase()).filter(Boolean));
  const groupOnly = groupMembers.filter(gm => gm.id && !storedIds.has(gm.id.toLowerCase()));

  const addFromGroup = async (gm: GroupMember) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await createProjectMember(pca, {
        projectId, userId: gm.id, userName: gm.displayName,
        userEmail: gm.mail ?? gm.userPrincipalName ?? null,
        role: ProjectMemberRole.TeamMember, isExternal: false,
      });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to add member.'); }
    finally { setBusy(false); }
  };

  const add = async () => {
    if (!worker || busy) return;
    if ((members ?? []).some(m => m.dagrun_pm_userid === worker.aadObjectId)) {
      setError(`${worker.displayName} is already a member of this project.`); return;
    }
    setBusy(true); setError(null);
    try {
      await createProjectMember(pca, {
        projectId, userId: worker.aadObjectId, userName: worker.displayName,
        userEmail: worker.mail ?? worker.userPrincipalName ?? null, role: addRole, isExternal: false,
      });
      setWorker(null); setAddRole(ProjectMemberRole.TeamMember);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to add member.'); }
    finally { setBusy(false); }
  };

  const changeRole = async (m: ProjectMember, newRole: number) => {
    if (busy || newRole === m.dagrun_pm_role) return;
    setBusy(true); setError(null);
    // Optimistic; guarded op re-reads live PM count and throws LAST_PM_MESSAGE if
    // this would demote the last PM.
    try { await setProjectMemberRoleGuarded(pca, projectId, m, newRole); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to change role.'); }
    finally { setBusy(false); }
  };

  const remove = async (m: ProjectMember) => {
    if (busy) return;
    setBusy(true); setError(null);
    try { await removeProjectMemberGuarded(pca, projectId, m); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to remove member.'); }
    finally { setBusy(false); }
  };

  const isPM = (m: ProjectMember) => m.dagrun_pm_role === ProjectMemberRole.ProjectManager;

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}>TEAM &amp; ROLES</div>
      <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 12px', lineHeight: 1.5 }}>
        Everyone on this project and their role. Project Managers can add or remove members and
        change roles. Every project must always have at least one Project Manager.
      </p>
      {error && <div style={{ fontSize: 12, color: '#DC2626', marginBottom: 10 }}>{error}</div>}

      {members === null ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>
      ) : members.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 12 }}>No members yet.</div>
      ) : (
        <div style={{ marginBottom: canManage ? 14 : 0 }}>
          {members.map(m => (
            <div key={m.dagrun_pm_projectmemberid}
              style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 10, padding: '8px 0', borderTop: '0.5px solid var(--border-light)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ minWidth: 0, fontSize: 13, color: 'var(--ink)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.dagrun_pm_username ?? m.dagrun_pm_useremail ?? (m.dagrun_pm_userid ?? '').slice(0, 8) + '…'}
                  </span>
                  {isPM(m) && (
                    <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                      color: 'var(--brand)', background: 'var(--brand-mist)', padding: '2px 7px', borderRadius: 999 }}>PM</span>
                  )}
                  {m.dagrun_pm_isexternal && <span style={{ flexShrink: 0, fontSize: 10, color: 'var(--ink-4)' }}>external</span>}
                </div>
                {m.dagrun_pm_useremail && <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.dagrun_pm_useremail}</div>}
              </div>
              {canManage ? (
                <>
                  {/* width:150 is REQUIRED to override the global `select { width:100% }`
                      rule in global.css — flexShrink:0 alone leaves the select at
                      100% and it swallows the whole row, hiding the name. */}
                  <select value={m.dagrun_pm_role} disabled={busy}
                    onChange={e => changeRole(m, Number(e.target.value))}
                    style={{ flexShrink: 0, width: 150, padding: '5px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface)', color: 'var(--ink-2)' }}>
                    {ROLE_OPTIONS.map(r => (
                      <option key={r} value={r}
                        disabled={isPM(m) && r !== ProjectMemberRole.ProjectManager && pmCount <= 1}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                  <button type="button" title={isPM(m) && pmCount <= 1 ? LAST_PM_MESSAGE : 'Remove member'}
                    onClick={() => remove(m)} disabled={busy}
                    style={{ flexShrink: 0, background: 'transparent', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 15 }}>×</button>
                </>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{ROLE_LABEL[m.dagrun_pm_role] ?? '—'}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {groupOnly.length > 0 && (
        <div style={{ marginBottom: canManage ? 14 : 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '4px 0 2px' }}>
            From the linked Microsoft 365 Group
          </div>
          {groupOnly.map(gm => (
            <div key={gm.id}
              style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 10, padding: '8px 0', borderTop: '0.5px solid var(--border-light)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ minWidth: 0, fontSize: 13, color: 'var(--ink)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {gm.displayName}
                  </span>
                  <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                    color: 'var(--ink-3)', background: 'var(--surface-2)', padding: '2px 7px', borderRadius: 999 }}>via Group</span>
                </div>
                {(gm.mail ?? gm.userPrincipalName) && (
                  <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gm.mail ?? gm.userPrincipalName}</div>
                )}
              </div>
              {canManage ? (
                <button type="button" className="btn-secondary" onClick={() => addFromGroup(gm)} disabled={busy}
                  style={{ flexShrink: 0, fontSize: 12, padding: '5px 12px' }}>Add to project</button>
              ) : (
                <span style={{ flexShrink: 0, fontSize: 12, color: 'var(--ink-3)' }}>{ROLE_LABEL[ProjectMemberRole.TeamMember]}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', borderTop: '0.5px solid var(--border-light)', paddingTop: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: 'var(--ink-4)', display: 'block', marginBottom: 4 }}>Add member</label>
            <PeoplePicker pca={pca} value={worker} onChange={setWorker} placeholder="Search Microsoft 365 users…" />
          </div>
          <div style={{ width: 160, flexShrink: 0 }}>
            <label style={{ fontSize: 11, color: 'var(--ink-4)', display: 'block', marginBottom: 4 }}>Role</label>
            <select value={addRole} onChange={e => setAddRole(Number(e.target.value))}
              style={{ padding: '8px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--surface)', color: 'var(--ink)' }}>
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
          </div>
          <button className="btn-primary" onClick={add} disabled={!worker || busy}>{busy ? 'Adding…' : 'Add'}</button>
        </div>
      )}
    </div>
  );
}
